import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import './Home.css';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';


const Home = () => {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [showPrivacy, setShowPrivacy] = useState(false);
  // Until the official emblem is dropped at /public/cet-logo.png, show a clean
  // cobalt "CET" monogram instead of a broken-image icon.
  const [logoError, setLogoError] = useState(false);


  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (!loading && user) {
      if (user.role === 'superadmin' || user.role === 'cetadmin') {
        navigate('/super-admin');
      } else if (user.role === 'cetcoordinator') {
        navigate('/coordinator');
      } else if (user.role === 'moderator') {
        navigate('/moderator');
      } else if (user.role === 'subject_matter_expert') {
        navigate('/subject-matter-expert');
      } else if (user.role === 'examiner') {
        navigate('/examiner');
      }
    }
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const getRoleCards = () => {
    if (!user) return [];
    const cardConfigs = {
      examiner: [
        { id: 1, title: 'Upload Question Paper', description: 'Upload an existing exam PDF to extract its paper structure', action: 'Upload PDF', path: '/extract-template', color: '#0D529B', details: 'Extract paper structure, questions, and metadata' },
        { id: 2, title: 'My Templates', description: 'View and reuse extracted paper structures', action: 'View Templates', path: '/paper-templates', color: '#1565C0', details: 'Access previously created templates' },
        { id: 3, title: 'Generate Question Paper', description: 'Generate question variants from your approved template', action: 'Start Paper', path: '/examiner/create', color: '#0A437D', details: 'Automated variant synthesis from the template' },
        { id: 4, title: 'My Papers', description: 'Continue or review question papers', action: 'View Papers', path: '/examiner/papers', color: '#64748B', details: 'Manage draft and completed papers' },
      ],
      subject_matter_expert: [
        { id: 1, title: 'Papers for Review', description: 'Review generated question variations', action: 'Review Papers', path: '/sme-papers', color: '#0D529B', details: 'Evaluate question quality' },
        { id: 2, title: 'Select Variations', description: 'Select best variations for each question', action: 'Select Questions', path: '/sme-papers', color: '#1565C0', details: 'Choose optimal variations' },
        { id: 3, title: 'Submit to Moderator', description: 'Forward selected questions to moderator', action: 'Submit', path: '/sme-papers', color: '#0A437D', details: 'Complete SME review' },
      ],
      moderator: [
        { id: 1, title: 'SME Approved Papers', description: 'Papers approved by Subject Matter Expert', action: 'View Papers', path: '/moderator-categorization', color: '#0D529B', details: 'Review approved papers' },
        { id: 2, title: 'Generate Exam Sets', description: 'Assemble 40 verified exam sets', action: 'Generate Sets', path: '/moderator-categorization', color: '#1565C0', details: 'Create exam variations' },
        { id: 3, title: 'Categorize Sets', description: 'Mark sets as General, Special Case, or Re-Exam', action: 'Categorize', path: '/moderator-categorization', color: '#0A437D', details: 'Classify exam sets' },
        { id: 4, title: 'Download Encrypted PDFs', description: 'Download secured exam papers', action: 'Download', path: '/moderator-categorization', color: '#64748B', details: 'Export encrypted papers' },
      ],
    };
    return cardConfigs[user.role] || [];
  };

  // The real CET QPG lifecycle: a paper moves Examiner -> SME -> Moderator,
  // with the Super Admin governing the whole platform. (Matches the official
  // CET QPG user guide.)
  const roleMatrix = [
    {
      role: 'Examiner',
      tag: 'Authoring',
      phases: [
        { phase: 'Stage 1', name: 'Create Paper Structure', desc: 'Defines the paper from a saved template or manual setup — subject, topic, difficulty, question count, total marks, and question types.' },
        { phase: 'Stage 2', name: 'Generate 40+ Variations', desc: 'Produces at least 40 distinct variations for every question and sub-question, spread across Easy / Medium / Hard with reference sources and a quality score.' },
        { phase: 'Stage 3', name: 'Send to SME', desc: 'Confirms the paper and forwards the full variation pool for expert review.' },
      ],
    },
    {
      role: 'Subject Matter Expert',
      tag: 'Validation',
      phases: [
        { phase: 'Stage 4', name: 'Review Variations', desc: 'Reviews every variation for quality and accuracy, selects the suitable ones, marks the rest, and can add comments — never dropping below 40.' },
        { phase: 'Stage 5', name: 'Forward to Moderator', desc: 'Sends the reviewed paper onward once all variations across every question have been evaluated.' },
      ],
    },
    {
      role: 'Moderator',
      tag: 'Assembly & Release',
      phases: [
        { phase: 'Stage 6', name: 'Assemble 40 Unique Sets', desc: 'Builds 40 complete paper sets — one variation per slot, every combination unique — so no two papers are identical.' },
        { phase: 'Stage 7', name: 'Categorize Sets', desc: 'Distributes the 40 sets across General, Re-exam, and Special categories.' },
        { phase: 'Stages 8–9', name: 'Finalize & Export', desc: 'Finalizes the base paper and downloads each set as a securely formatted PDF (EN / HI / MR / UR).' },
      ],
    },
    {
      role: 'CET Admin',
      tag: 'Governance',
      phases: [
        { phase: 'Oversight', name: 'Approvals & Controls', desc: 'Approves moderators, enforces one active SME and one Moderator per subject, manages system-wide settings, and retains full audit visibility.' },
      ],
    },
  ];

  const getDashboardPath = () => {
    if (!user) return '/login';
    if (user.role === 'superadmin' || user.role === 'cetadmin') return '/super-admin';
    if (user.role === 'cetcoordinator') return '/coordinator';
    if (user.role === 'moderator') return '/moderator';
    if (user.role === 'subject_matter_expert') return '/subject-matter-expert';
    if (user.role === 'examiner') return '/examiner';
    return '/';
  };

  const roleCards = getRoleCards();
  const handleCardClick = (path) => navigate(path);

  return (
    <div className="home-container">
      <div className="animated-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>
      <header className={`home-header ${isVisible ? 'visible' : ''}`}>
        <div className="header-left">
          <div className="header-logo">
            {/* Official State CET Cell emblem. Pointed at the existing asset in
                qpgenerationclient/public/. To use a different file, drop it in
                public/ and update the src below (e.g. "/cet-logo.png"). If the
                path is wrong/missing, the cobalt "CET" monogram shows instead. */}
            {!logoError ? (
              <img
                className="cet-logo"
                src="/favicon.png"
                alt="State Common Entrance Test Cell, Maharashtra"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="cet-logo cet-logo-placeholder">CET</div>
            )}
            <div className="brand-text-block">
              <span className="brand-line1">STATE COMMON ENTRANCE TEST CELL</span>
              <span className="brand-line2">Government of Maharashtra</span>
            </div>
          </div>
        </div>
        <div className="header-right">
          {user ? (
            <>
              <div className="role-badge">
                <span className="badge-value">{user.role.replace(/_/g, ' ').toUpperCase()}</span>
              </div>
              <span className="user-name">{user.name}</span>
            </>
          ) : (
            <button className="btn btn-primary header-login" onClick={() => navigate('/login')}>
              Access Platform Login
            </button>
          )}
        </div>
      </header>

      <main className={`home-main ${isVisible ? 'visible' : ''}`}>
        {/* Hero Section - Show for all */}
        <section className="hero-section">
          <div className="hero-left">
            <div className="hero-badge">State Common Entrance Test Cell · Maharashtra</div>
            <h2 className="hero-title">
              Secure Automated
              <br />
              <span className="gradient-text">Question Paper Generation System</span>
            </h2>
            <p className="hero-subtitle">An institutional validation assembly line for generating, reviewing, and verifying exam papers with strict academic structural control, multi-level human evaluation, and automated cryptographic compilation workflows.</p>
            <div className="hero-buttons">
              <button className="btn btn-primary" onClick={() => navigate(getDashboardPath())}>
                {user ? 'Go to Dashboard' : 'Access Platform Login'}
              </button>
            </div>
          </div>
          <div className="hero-right">
            <div className="audit-card">
              <div className="audit-card-header">
                <div className="audit-title-group">
                  <span className="audit-status-dot"></span>
                  <h3>System Live Audit Metrics</h3>
                </div>
                <span className="audit-secure-tag">Secure</span>
              </div>
              <div className="audit-grid">
                <div className="audit-cell">
                  <span className="audit-value">10,000+</span>
                  <span className="audit-label">Validated Question Bank Base</span>
                </div>
                <div className="audit-cell">
                  <span className="audit-value">1,200+</span>
                  <span className="audit-label">Verified Structural Templates</span>
                </div>
                <div className="audit-cell">
                  <span className="audit-value">40</span>
                  <span className="audit-label">Cryptographically Finalized Sets</span>
                </div>
              </div>
              <div className="audit-card-footer">
                <span className="audit-footer-dot"></span>
                Multi-tier verification active · Government of Maharashtra
              </div>
            </div>
          </div>
        </section>

        {/* Role-Based Action Cards - Show only for authenticated */}
        {user && roleCards.length > 0 && (
          <section className="action-cards-section">
            <div className="section-header">
              <h3 className="section-title">Your Actions</h3>
              <p className="section-subtitle">Quick access to your role-specific tasks</p>
            </div>
            <div className="action-cards-grid">
              {roleCards.map((card, index) => (
                <div key={card.id} className={`action-card ${expandedCard === card.id ? 'expanded' : ''}`} style={{ '--card-color': card.color, '--delay': `${index * 0.1}s` }} onClick={() => handleCardClick(card.path)} onMouseEnter={() => setExpandedCard(card.id)} onMouseLeave={() => setExpandedCard(null)}>
                  <div className="card-top">
                    <h4 className="card-title">{card.title}</h4>
                  </div>
                  <p className="card-description">{card.description}</p>
                  {expandedCard === card.id && <p className="card-details">{card.details}</p>}
                  <button className="card-button">{card.action}</button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Platform Preview - branding showcase */}
        <section className="showcase-section">
          <div className="showcase-head">
            <h3 className="section-title">One platform. Every role.</h3>
            <p className="showcase-subtitle">
              A unified, secure workspace where examiners, subject experts, and moderators
              collaborate to produce verified question papers — end to end.
            </p>
          </div>
          <div className="showcase-frame">
            <div className="showcase-bar">
              <div className="showcase-dots"><span></span><span></span><span></span></div>
              <div className="showcase-url">qpg.cet.org/dashboard</div>
            </div>
            <img src="/cetadminsnippet.PNG" alt="CET QPG administration dashboard" className="showcase-img" />
          </div>
        </section>

        {/* Role Matrix - the 7-phase lifecycle mapped to the 4 responsibilities */}
        <section className="rolematrix-section">
          <div className="section-header">
            <h3 className="section-title">A governed, multi-role lifecycle</h3>
            <p className="section-subtitle">Every paper moves from Examiner to SME to Moderator — from authoring 40+ variations to assembling and securely exporting 40 unique sets, all under Super Admin governance.</p>
          </div>
          <div className="rolematrix-grid">
            {roleMatrix.map((col, ci) => (
              <div key={ci} className="rolematrix-col">
                <div className="rolematrix-card">
                  <div className="rolematrix-head">
                    <span className="rolematrix-index">{String(ci + 1).padStart(2, '0')}</span>
                    <div className="rolematrix-head-text">
                      <h4 className="rolematrix-role">{col.role}</h4>
                      <span className="rolematrix-tag">{col.tag}</span>
                    </div>
                  </div>
                  <div className="rolematrix-phases">
                    {col.phases.map((p, pi) => (
                      <div key={pi} className="rolematrix-phase">
                        <span className="rolematrix-phase-tag">{p.phase}</span>
                        <h5 className="rolematrix-phase-name">{p.name}</h5>
                        <p className="rolematrix-phase-desc">{p.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="home-footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-text">Authorized Examination System</p>

            <div className="footer-links">
              <button
                className="footer-link"
                onClick={() => setShowPrivacy(true)}
              >
                Privacy & AI Disclaimer
              </button>
            </div>
            {showPrivacy &&
              createPortal(
                <div className="privacy-overlay" onClick={() => setShowPrivacy(false)}>
                  <div
                    className="privacy-modal"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3>Privacy & AI Disclaimer</h3>

                    <p>
                      This system uses Artificial Intelligence (AI) and Large Language Models (LLMs)
                      to assist in generating examination questions and variations.
                    </p>

                    <p>
                      <strong>Important Notice:</strong> AI-generated content may contain
                      inaccuracies or unintended errors.
                    </p>

                    <ul>
                      <li>All questions must be reviewed by the Examiner and SME.</li>
                      <li>No paper is finalized without human approval.</li>
                      <li>AI assists but does not replace academic judgment.</li>
                    </ul>

                    <p className="privacy-note">
                      By using this system, users acknowledge and accept these conditions.
                    </p>

                    <button
                      className="privacy-close"
                      onClick={() => setShowPrivacy(false)}
                    >
                      <MdClose className="ui-icon" aria-hidden="true" /> Close
                    </button>
                  </div>
                </div>,
                document.body
              )
            }



            <p className="footer-copyright">
              © 2024 CET QPG · State Common Entrance Test Cell, Maharashtra. All rights reserved.
            </p>
          </div>

        </div>
      </footer>

      <div className="particles">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="particle" style={{ '--delay': `${i * 0.15}s` }}></div>
        ))}
      </div>
    </div>
  );
};

export default Home;
